import { PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack, ThemeProvider } from "@mui/material";
import { useWorkplacePhotos } from "@src/appV2/Facilities/api/useWorkplacePhotos";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useEnableGeofenceTracking } from "@src/appV2/Location/geofence/hooks/useEnableGeofenceTracking";
import { GeofenceProvider } from "@src/appV2/Location/geofence/useGeofence";
import { ChartingCredentialsSetUpContainer } from "@src/appV2/redesign/Charting/ChartingCredentialsSetUpContainer";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { SwipeableViewsWithDots } from "@src/appV2/redesign/components/SwipeableViewsWithDots";
import { type UserLocationState } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { PageWrapper } from "../../../components/PageWrapper";
import { PageContent } from "../../components/PageContent";
import { MY_SHIFTS_FULL_PATH } from "../../path";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { getGradientByShiftPeriod } from "../utils/getGradientByShiftPeriod";
import { TimekeepingOptionsButton } from "./components/TimekeepingOptionsButton";
import { FocusModeOfferBottomSheet } from "./FocusMode/components/FocusModeOfferBottomSheet";
import { FocusModeProvider, useFocusModeContext } from "./FocusMode/hooks/useFocusModeState";
import { TimekeepingDetailsSubPage } from "./TimekeepingDetailsSubPage";
import { TimekeepingFocusSubPage } from "./TimekeepingFocusSubPage";

interface FocusModeState {
  isActive: boolean;
  bonusPoints: number;
  shiftEndAt: string;
  optIn: () => Promise<void>;
}

interface ShiftTimekeepingPageContentProps {
  justClockedIn: boolean;
  focusModeState?: FocusModeState;
}

function ShiftTimekeepingPageContent({
  justClockedIn,
  focusModeState,
}: ShiftTimekeepingPageContentProps) {
  const { shiftId } = useParams<{ shiftId: string }>();
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(0);

  useLogEffect(
    APP_V2_USER_EVENTS.TIMEKEEPING_PAGE_VIEWED,
    { shiftId },
    { enabled: isDefined(shiftId) }
  );

  const clockOutModalState = useModalState();
  const focusModeOfferModalState = useModalState();

  useEffect(() => {
    if (justClockedIn && isDefined(focusModeState)) {
      focusModeOfferModalState.openModal();
    }
    // Only trigger on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { shift, shiftState, shiftError } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  const currentDate = useCurrentDate(1000);

  const isShiftDetailsVisible = activeStep === 1;

  const { data: facilityPhotos } = useWorkplacePhotos(
    { workplaceId: shift?.facility?.userId ?? "" },
    { enabled: isDefined(shift?.facility?.userId) }
  );

  const isPageDataError = isDefined(shiftError);

  const workplaceImage = facilityPhotos?.photosList?.[0]?.url;

  function handleStepChange(step: number) {
    setActiveStep(step);

    // Log subpage navigation
    if (isDefined(shiftId)) {
      if (step === 0) {
        logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_FOCUS_SUBPAGE_VIEWED, { shiftId });
      } else if (step === 1) {
        logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_DETAILS_SUBPAGE_VIEWED, { shiftId });
      }
    }
  }

  function handleBack() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_BACK_BUTTON_TAPPED, {
      shiftId,
      activeStep,
      destination: activeStep === 0 ? "my-shifts" : "focus-view",
    });
    if (activeStep === 0) {
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      setActiveStep(0);
    }
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => {
          const timeSlotGradient = isDefined(shift?.name)
            ? getGradientByShiftPeriod(shiftPeriodToTimeSlot[shift.name], theme)
            : undefined;

          return {
            background: timeSlotGradient
              ? `linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 50%), ${timeSlotGradient}`
              : "linear-gradient(180deg, #CCE7FF 0%, #F6F3F1 100%)",
            transition: theme.transitions.create(["background"]),
            minHeight: "100vh",
          };
        }}
      >
        {/* TODO: Replace with StickyScrollPageHeader / PageHeaderTopRow */}
        <PageHeaderWrapper sx={{ paddingX: 4, background: "transparent" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={6}
            width="100%"
          >
            <IconButton
              aria-label="Go Back"
              title="Go Back"
              iconType="chevron-left"
              variant="outlined"
              onClick={() => {
                handleBack();
              }}
            />
            <TimekeepingOptionsButton
              shiftId={shiftId}
              workplaceId={shift?.facilityId}
              clockOutModalState={clockOutModalState}
              isShiftDetailsVisible={isShiftDetailsVisible}
              onShowShiftDetails={() => {
                setActiveStep(1);
              }}
              onShowShiftStatus={() => {
                setActiveStep(0);
              }}
            />
          </Stack>
        </PageHeaderWrapper>
        <PageContent>
          {isPageDataError ? (
            <Alert sx={{ marginY: 4 }} severity="error">
              Something went wrong while loading this shift. Please try again.
            </Alert>
          ) : null}

          {isDefined(shift) && isDefined(shiftState) && (
            <SwipeableViewsWithDots
              activeStep={activeStep}
              onChangeStep={(step) => {
                handleStepChange(step);
              }}
            >
              <TimekeepingFocusSubPage
                shift={shift}
                shiftState={shiftState}
                workplaceImage={workplaceImage}
                workplaceName={shift.facility.name}
                currentDate={currentDate}
                clockOutModalState={clockOutModalState}
                focusModeState={
                  focusModeState
                    ? {
                        isActive: focusModeState.isActive,
                        bonusPoints: focusModeState.bonusPoints,
                        shiftEndAt: focusModeState.shiftEndAt,
                      }
                    : undefined
                }
              />
              <TimekeepingDetailsSubPage shift={shift} shiftState={shiftState} />
            </SwipeableViewsWithDots>
          )}
          {isDefined(focusModeState) && isDefined(shiftId) && (
            <FocusModeOfferBottomSheet
              modalState={focusModeOfferModalState}
              shiftId={shiftId}
              onOptIn={() => {
                void focusModeState.optIn();
              }}
            />
          )}
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}

export function ShiftTimekeepingPage() {
  const { shiftId } = useParams<{ shiftId: string }>();
  const { userId } = useDefinedWorker();
  const initialShiftStateLocation = useRef<UserLocationState | undefined>(undefined);

  const { shift, shiftState } = useGetShiftDetailsData({
    shiftId: shiftId ?? "",
  });

  if (isDefined(shiftState) && !initialShiftStateLocation.current) {
    initialShiftStateLocation.current = shiftState.metadata.userLocationState;
  }

  const facilityGeoLocation = isDefined(shift?.facility.geoLocation.coordinates)
    ? convertToGeoLocation(shift?.facility.geoLocation.coordinates)
    : undefined;

  const enableGeofenceTracking = useEnableGeofenceTracking({
    shiftStart: shift?.start,
    shiftState,
  });

  const location = useLocation();

  function hasClockedOutBoolean(state: unknown): state is { clockedOut: boolean } {
    return (
      typeof state === "object" &&
      state !== null &&
      "clockedOut" in state &&
      typeof state.clockedOut === "boolean"
    );
  }

  const justClockedOut = hasClockedOutBoolean(location.state) ? location.state.clockedOut : false;

  function hasJustClockedInBoolean(state: unknown): state is { justClockedIn: boolean } {
    return (
      typeof state === "object" &&
      state !== null &&
      "justClockedIn" in state &&
      typeof state.justClockedIn === "boolean"
    );
  }

  const justClockedIn = hasJustClockedInBoolean(location.state)
    ? location.state.justClockedIn
    : false;

  const isFocusModeEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_FOCUS_MODE, {
    defaultValue: false,
  });

  return (
    <GeofenceProvider
      shiftId={shiftId ?? ""}
      workerId={userId}
      workplaceId={shift?.facilityId}
      initialLocation={initialShiftStateLocation.current}
      fallbackGeofenceLocation={facilityGeoLocation}
      enableGeofenceTracking={enableGeofenceTracking}
    >
      {isFocusModeEnabled && isDefined(shiftId) ? (
        <FocusModeProvider shiftId={shiftId}>
          <FocusModeAwareContent justClockedIn={justClockedIn} />
        </FocusModeProvider>
      ) : (
        <ShiftTimekeepingPageContent justClockedIn={justClockedIn} />
      )}
      {!justClockedOut && isDefined(shift?.facilityId) && (
        <ChartingCredentialsSetUpContainer shiftId={shiftId} workplaceId={shift.facilityId} />
      )}
    </GeofenceProvider>
  );
}

function FocusModeAwareContent({ justClockedIn }: { justClockedIn: boolean }) {
  const focusModeContext = useFocusModeContext();

  return (
    <ShiftTimekeepingPageContent justClockedIn={justClockedIn} focusModeState={focusModeContext} />
  );
}
